/**
 * ============================================================================
 * useDispositionTasksQuery — Phase 6a frontend (T-P6a-W3-03)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for the records-disposition tray. Returns the
 *          full task list — the screen filters by tab client-side so badge
 *          counts and pagination stay coherent across tab switches.
 * Endpoint: GET /api/v1/disposition/tasks
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type DispositionTaskState =
  | 'PENDING'
  | 'AWAITING_SIGNATURES'
  | 'READY'
  | 'BLOCKED'
  | 'COMPLETED';

export type DispositionAction = 'DESTROY' | 'ARCHIVE' | 'EXPORT';

export type DispositionBlockedReason = 'OPEN_CAPA' | 'PENDING_CC' | 'OPEN_LEGAL_HOLD';

export type SignerRole = 'QA_MANAGER' | 'DOC_CONTROLLER';

export interface DispositionSigner {
  role: SignerRole;
  signed_at_utc: string | null;
  signer_user_id: string | null;
}

export interface DispositionTask {
  task_id: string;
  document_id: string;
  document_title: string | null;
  doc_number: string;
  policy_id: string;
  policy_name: string;
  action: DispositionAction;
  state: DispositionTaskState;
  retention_end_at_utc: string;
  cooling_off_until_utc: string | null;
  signers: DispositionSigner[];
  blocked_reason: DispositionBlockedReason | null;
}

export interface DispositionTasksResponse {
  items: DispositionTask[];
}

export function useDispositionTasksQuery(): UseQueryResult<DispositionTasksResponse> {
  return useQuery<DispositionTasksResponse>({
    queryKey: ['disposition', 'tasks'],
    queryFn: ({ signal }) =>
      apiFetch<DispositionTasksResponse>('/api/v1/disposition/tasks', { signal }),
  });
}
